/**
 * <brick-header>
 * - Desktop dropdown / mega panels (<details>): one open at a time, hover
 *   intent on fine pointers, Esc + outside click close, aria-expanded sync.
 * - Mobile menu drawer (<dialog>): focus trap, Esc/backdrop close, exit
 *   animation, focus returns to the toggle.
 * - Sticky modes: "always" (adds a soft shadow once scrolled) and
 *   "scroll-up" (hides on scroll down, reveals on scroll up).
 * - Cart count bubble: listens for `brick:cart:updated` ({ itemCount }).
 */
(() => {
  if (customElements.get('brick-header')) return;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  const motionOff = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = window.matchMedia('(min-width: 990px)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  class BrickHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.setupDropdowns();
      this.setupDrawer();
      this.setupSticky();

      this.onCartUpdated = (event) => this.updateCount(event.detail);
      document.addEventListener('brick:cart:updated', this.onCartUpdated);
    }

    disconnectedCallback() {
      document.removeEventListener('brick:cart:updated', this.onCartUpdated);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('scroll', this.onScroll);
      desktop.removeEventListener('change', this.onBreakpoint);
      if (this.sentinel) this.sentinel.remove();
    }

    /* ---------------------------------------------------------------- */
    /* Dropdowns                                                          */
    /* ---------------------------------------------------------------- */
    setupDropdowns() {
      this.dropdowns = Array.from(this.querySelectorAll('details[data-header-dropdown]'));
      if (!this.dropdowns.length) return;

      this.dropdowns.forEach((details) => {
        const summary = details.querySelector('summary');
        summary.setAttribute('aria-expanded', 'false');
        summary.setAttribute('aria-haspopup', 'true');

        details.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', String(details.open));
          if (details.open) this.closeDropdowns(details);
        });

        // Focus leaving the panel closes it (keyboard users tabbing past).
        details.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !details.contains(event.relatedTarget)) details.open = false;
        });

        // Hover intent for mouse users; click still works everywhere.
        const item = details.parentElement;
        item.addEventListener('mouseenter', () => {
          if (!finePointer.matches) return;
          window.clearTimeout(details._closeTimer);
          if (!details.open) {
            details.open = true;
            details._hoverOpened = Date.now();
          }
        });
        item.addEventListener('mouseleave', () => {
          if (!finePointer.matches) return;
          details._closeTimer = window.setTimeout(() => {
            details.open = false;
          }, 160);
        });
        summary.addEventListener('click', (event) => {
          // Avoid closing a panel the user just opened by hovering.
          if (details.open && details._hoverOpened && Date.now() - details._hoverOpened < 600) {
            event.preventDefault();
          }
          details._hoverOpened = 0;
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = this.dropdowns.find((details) => details.open);
        if (!open) return;
        open.open = false;
        open.querySelector('summary').focus();
      });

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeDropdowns();
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    closeDropdowns(except) {
      (this.dropdowns || []).forEach((details) => {
        if (details !== except) details.open = false;
      });
    }

    /* ---------------------------------------------------------------- */
    /* Mobile drawer                                                      */
    /* ---------------------------------------------------------------- */
    setupDrawer() {
      this.drawer = this.querySelector('[data-menu-drawer]');
      this.toggle = this.querySelector('[data-menu-open]');
      if (!this.drawer || !this.toggle) return;

      this.toggle.addEventListener('click', () => this.openDrawer());
      this.drawer.addEventListener('click', (event) => {
        if (event.target === this.drawer || event.target.closest('[data-drawer-close]')) this.closeDrawer();
      });
      this.drawer.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeDrawer();
      });
      this.drawer.addEventListener('keydown', (event) => trapFocus(this.drawer, event));
      this.drawer.addEventListener('close', () => {
        this.drawer.classList.remove('is-closing');
        this.toggle.setAttribute('aria-expanded', 'false');
        if (!desktop.matches) this.toggle.focus({ preventScroll: true });
      });

      this.onBreakpoint = () => {
        if (desktop.matches && this.drawer.open) this.drawer.close();
      };
      desktop.addEventListener('change', this.onBreakpoint);
    }

    openDrawer() {
      if (this.drawer.open) return;
      document.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
      this.drawer.showModal();
      this.toggle.setAttribute('aria-expanded', 'true');
      const close = this.drawer.querySelector('[data-drawer-close]');
      if (close) close.focus({ preventScroll: true });
    }

    closeDrawer() {
      const dialog = this.drawer;
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      if (motionOff()) {
        dialog.close();
        return;
      }
      dialog.classList.add('is-closing');
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        dialog.close();
      };
      dialog.querySelector('.drawer__panel').addEventListener('animationend', finish, { once: true });
      window.setTimeout(finish, 400);
    }

    /* ---------------------------------------------------------------- */
    /* Sticky                                                             */
    /* ---------------------------------------------------------------- */
    setupSticky() {
      const mode = this.dataset.sticky;
      if (mode === 'none' || this.section === this) return;

      // Static marker right above the sticky wrapper: its position is the
      // header's natural top (offsetTop of a stuck element is unreliable).
      this.sentinel = document.createElement('div');
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.sentinel.style.height = '0';
      this.section.before(this.sentinel);

      let lastY = window.scrollY;
      let ticking = false;
      const update = () => {
        ticking = false;
        const y = window.scrollY;
        const top = this.sentinel.getBoundingClientRect().top + y;
        const height = this.offsetHeight;
        this.section.classList.toggle('is-scrolled', y > top + 1);

        if (mode === 'scroll-up') {
          const panelOpen = this.dropdowns && this.dropdowns.some((details) => details.open);
          if (y > lastY && y > top + height * 2 && !panelOpen) {
            this.section.classList.add('is-hidden');
          } else if (y < lastY - 4 || y <= top + height) {
            this.section.classList.remove('is-hidden');
          }
        }
        lastY = y;
      };

      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      // Keyboard focus inside a hidden header reveals it.
      this.addEventListener('focusin', () => this.section.classList.remove('is-hidden'));
      update();
    }

    /* ---------------------------------------------------------------- */
    /* Cart count                                                         */
    /* ---------------------------------------------------------------- */
    updateCount(detail) {
      if (!detail || typeof detail.itemCount !== 'number') return;
      const count = detail.itemCount;
      const bubble = this.querySelector('[data-cart-count]');
      const text = this.querySelector('[data-cart-count-text]');
      if (bubble) {
        const previous = parseInt(bubble.textContent, 10) || 0;
        bubble.textContent = String(Math.min(count, 99));
        bubble.hidden = count === 0;
        if (count > previous && !motionOff()) {
          bubble.classList.remove('is-bumped');
          void bubble.offsetWidth;
          bubble.classList.add('is-bumped');
        }
      }
      if (text && detail.countText) text.textContent = detail.countText;
    }
  }

  customElements.define('brick-header', BrickHeader);
})();
